import { useSyncExternalStore } from "react";
import { QUICK_REACTIONS } from "./reactions";

/**
 * Which emoji I use (reacting and typing), per person on this device, so the
 * quick bar and the picker's "Frequently used" can lead with them. A
 * convenience only: storage can be blocked or wiped, and then everything
 * falls back to the defaults.
 */

interface Usage {
  /** Times used. */
  n: number;
  /** Last used (ms). */
  at: number;
}

type UsageMap = Record<string, Usage>;

const storageKey = (userId: number) => `chat:emoji-usage:${userId}`;
/** Kept per person; the least used beyond this are forgotten. */
const MAX_TRACKED = 60;
const CHANGED = "chat:emoji-usage-changed";

const read = (userId: number): UsageMap => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as UsageMap) : {};
  } catch {
    return {};
  }
};

/** Most used first; ties go to the most recent. */
const ranked = (usage: UsageMap) =>
  Object.entries(usage)
    .filter(([, u]) => u && typeof u.n === "number")
    .sort(([, a], [, b]) => b.n - a.n || b.at - a.at)
    .map(([emoji]) => emoji);

export const recordEmojiUse = (userId: number | undefined, emoji: string, now = Date.now()) => {
  if (!userId || !emoji) return;
  const usage = read(userId);
  const prev = usage[emoji];
  usage[emoji] = { n: (prev?.n ?? 0) + 1, at: now };
  const keep = ranked(usage).slice(0, MAX_TRACKED);
  const trimmed: UsageMap = {};
  for (const e of keep) trimmed[e] = usage[e];
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(trimmed));
  } catch {
    // Storage blocked or full: the bar just keeps its defaults.
  }
  window.dispatchEvent(new Event(CHANGED));
};

/** My most used emoji, most used first (empty until I've used some). */
export const frequentEmojis = (userId: number | undefined, limit = 16): string[] =>
  userId ? ranked(read(userId)).slice(0, limit) : [];

/**
 * The quick bar's six: my most used first, then the usual six to fill it —
 * so it never shrinks and never repeats one.
 */
export const quickReactionsFor = (frequent: readonly string[], size = QUICK_REACTIONS.length) => {
  const out: string[] = [];
  for (const e of [...frequent, ...QUICK_REACTIONS]) {
    if (out.length >= size) break;
    if (!out.includes(e)) out.push(e);
  }
  return out;
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGED, onChange);
  // Another tab used one.
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
};

/** [frequentEmojis] that follows new uses (this tab and others). */
export const useFrequentEmojis = (userId: number | undefined, limit = 16): string[] => {
  // A string snapshot keeps useSyncExternalStore's identity check stable.
  const joined = useSyncExternalStore(
    subscribe,
    () => frequentEmojis(userId, limit).join("\u0000"),
    () => "",
  );
  return joined ? joined.split("\u0000") : [];
};
